import { Button, Icon, PageHeader } from '@lawpilots/design-system'
import { Box, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { DataList, PageLayout, ToolBar } from '@/common/components'
import { FEATURES } from '@/features.config'
import { MainCard } from '@/common/components/MainCard'
import type { MenusNavItem } from '@/common/types'
import {
  TenantHierarchyTable,
  TenantSearch,
  TenantSearchFormContext,
  tenantSlice,
  useSearchTenants,
} from '@/features/tenant'
import { config as appConfig } from '@/core'
import { usePageVisited } from '@/hooks'
import { useTypedDispatch } from '@/store'
import CustomPalette from '@/theme/CustomPalette'

const useNormalizedSearchTenants = (page: number, size: number) => {
  const result = useSearchTenants(page, size)
  const normalizedData = result.data
    ? Array.isArray(result.data)
      ? { items: result.data, total: result.data.length }
      : { items: result.data.items || [], total: result.data.total || 0 }
    : undefined

  return {
    data: normalizedData,
    isFetching: result.isFetching,
    isUninitialized: result.isUninitialized,
  }
}

const Tenants = (): JSX.Element => {
  const { t, i18n } = useTranslation('general')
  const dispatch = useTypedDispatch()
  usePageVisited('Tenants')
  const isGlobalAdminSidebarEnabled = appConfig.features.enableGlobalAdminSidebar

  const tenantMenus: MenusNavItem[] = [
    {
      index: '1',
      component: async () => {
        const module = await import(/* webpackChunkName: "TenantMenu" */ '@/features/tenant/menus/TenantMenu')
        return {
          default: module.TenantMenu,
        }
      },
      props: { title: t('createCustomer') },
    },
  ]

  const openMenu = () => {
    dispatch(tenantSlice.actions.openCreateTenantMenu(true))
  }

  const closeMenu = () => {
    dispatch(tenantSlice.actions.openCreateTenantMenu(false))
  }

  // Customer creation moved to the Radar app (FS-58). The button and the
  // create dialog stay in place so re-enabling is a one-line revert; the
  // laas-api CreateTenant route rejects submissions regardless.
  const createCustomerButton = (
    <Tooltip arrow title={t('create_customer_moved_to_radar')}>
      <span style={{ cursor: 'not-allowed', display: 'inline-block' }}>
        <Button disabled icon={<Icon name="plus" />} onClick={openMenu}>
          {t('createCustomer')}
        </Button>
      </span>
    </Tooltip>
  )

  return (
    <PageLayout feature={FEATURES.TENANTS}>
      <PageHeader title={t('customerOverview')} />
      <ToolBar buttonOnly menus={tenantMenus} closeAction={closeMenu} key={i18n.language} />
      <TenantSearchFormContext>
        {isGlobalAdminSidebarEnabled ? (
          <MainCard
            sx={{ border: '1px solid', borderColor: CustomPalette.Air }}
            contentSX={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <TenantSearch placeholder={t('search_customers')} />
              {createCustomerButton}
            </Box>
            <TenantHierarchyTable />
          </MainCard>
        ) : (
          <MainCard sx={{ border: '1px solid', borderColor: CustomPalette.Air }}>
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <TenantSearch placeholder={t('search')} />
              {createCustomerButton}
            </Box>
            <DataList
              emptyList={t('noTenantsFound')}
              loadingLabel={t('customers_loading')}
              titles={[t('company')]}
              useSearchList={useNormalizedSearchTenants}
            />
          </MainCard>
        )}
      </TenantSearchFormContext>
    </PageLayout>
  )
}

export default Tenants
